import type { Route } from './+types/selling';
import type { FaqItem } from '@/components/sections/faq-section';
import { ContentSection } from '@/components/sections/content-section';
import { CtaBanner } from '@/components/sections/cta-banner';
import { FaqSection } from '@/components/sections/faq-section';
import { HeroSection } from '@/components/sections/hero-section';
import { ImageBanner } from '@/components/sections/image-banner';
import { ImageGallery } from '@/components/sections/image-gallery';
import { ProcessSteps } from '@/components/sections/process-steps';
import { ValueProposition } from '@/components/sections/value-proposition';
import { buildFaqPageSchema, inheritRootLdJson, ldJsonMeta } from '@/utils/schema';

// TODO: drop the real photos in at these paths (see chat for details):
// - public/static/selling/aerial-farmland.jpg
// - public/static/selling/street-at-dusk.jpg
const AERIAL_FARMLAND = '/static/selling/aerial-farmland.jpg';
const STREET_AT_DUSK = '/static/selling/street-at-dusk.jpg';

const SELLING_FAQS: Array<FaqItem> = [
  {
    id: 'property-worth',
    question: 'How do I know what my property is worth?',
    answer:
      'The best way is a professional appraisal. Lucio will assess your property in person and provide a realistic price guide based on current market evidence — not guesswork.',
  },
  {
    id: 'renovate-before-selling',
    question: 'Should I renovate before selling?',
    answer:
      "Not always. Some improvements add value; others don't recoup their cost. Lucio will give you practical, unbiased advice on what's worth doing before you spend a dollar.",
  },
  {
    id: 'method-of-sale',
    question: 'What method of sale is best — auction, private treaty or expressions of interest?',
    answer:
      "It depends on your property and the current market. We'll talk through the pros and cons of each approach and recommend what's most likely to achieve the best result for your specific situation.",
  },
  {
    id: 'time-to-sell',
    question: 'How long will it take to sell?',
    answer:
      'Timeframes vary depending on price, property type and buyer demand. We’ll give you an honest expectation from the outset and work hard to minimise days on market without compromising your outcome.',
  },
  {
    id: 'fees',
    question: 'What are your fees?',
    answer: "We'll be upfront about fees from the very first conversation. No surprises, no hidden costs.",
  },
];

export function meta({ matches }: Route.MetaArgs) {
  return [
    ...inheritRootLdJson(matches),
    { title: 'Selling | Meet Real Estate — Kingaroy & South Burnett' },
    {
      name: 'description',
      content:
        'Selling a property in Kingaroy or the South Burnett? Meet Real Estate gives you honest appraisals, a tailored strategy and direct, personal service from Lucio Passos — start to settlement.',
    },
    ldJsonMeta(buildFaqPageSchema(SELLING_FAQS)),
  ];
}

export default function Selling() {
  return (
    <main>
      <HeroSection
        eyebrow="Selling"
        heading="Selling With Meet Real Estate"
        description="We turn up, when others don't."
        primaryCta={{ label: 'Request an appraisal', to: '/appraisal' }}
      />

      <ImageBanner
        eyebrow="Local Expertise"
        heading="Selling in the South Burnett"
        description="From family homes in town to acreage on the edge of the ranges — we know this market because we live in it."
        image={AERIAL_FARMLAND}
        dim
      />

      <ContentSection
        id="shows-up"
        className="scroll-mt-20"
        heading="You Deserve an Agent Who Shows Up"
        image={STREET_AT_DUSK}
        imageAlt="A Kingaroy street at dusk"
        paragraphs={[
          "Selling a property is one of the biggest financial decisions you'll make. You deserve more than a sign in the ground and a listing on a website.",
          'At Meet Real Estate, you deal directly with Lucio Passos from the first conversation to settlement. No handoffs. No junior staff. Just consistent, personal service from someone who genuinely cares about the outcome.',
        ]}
      />

      <ValueProposition
        id="what-to-expect"
        className="section--navy-mid scroll-mt-20"
        eyebrow="What to Expect"
        heading="What to Expect When You Sell With Us"
        align="center"
        items={[
          {
            title: 'An honest appraisal, not a flattering one',
            description:
              "We'll give you a clear, realistic assessment of your property's value based on current market conditions in the South Burnett — not an inflated number designed to win your listing.",
          },
          {
            title: 'A tailored strategy',
            description:
              "Every property is different. We'll discuss timing, presentation, method of sale and marketing that fits your property and your goals — not a one-size-fits-all approach.",
          },
          {
            title: 'Clear communication, always',
            description:
              "You'll never be left wondering what's happening. Lucio keeps you informed at every stage, so you can make decisions with confidence.",
          },
          {
            title: 'Negotiation that works for you',
            description:
              '26 years of sales experience across competitive international markets means proven negotiation skill in every transaction — working to get you the best possible result.',
          },
        ]}
      />

      <ProcessSteps
        id="process"
        className="scroll-mt-20"
        eyebrow="How It Works"
        heading="Our Selling Process"
        steps={[
          {
            title: 'Confidential Consultation',
            description:
              'We start with a no-obligation conversation to understand your property, your situation and your goals.',
          },
          {
            title: 'Property Appraisal',
            description:
              'Lucio provides an honest market appraisal and walks you through recent comparable sales in your area.',
          },
          {
            title: 'Marketing Strategy',
            description:
              'We develop a tailored marketing plan — covering photography, listing presentation, digital exposure and local reach.',
          },
          {
            title: 'Buyer Engagement',
            description:
              'We actively engage qualified buyers, follow up every enquiry and keep you updated throughout.',
          },
          {
            title: 'Negotiation & Offers',
            description:
              'When offers come in, Lucio negotiates firmly on your behalf to achieve the strongest possible outcome.',
          },
          {
            title: 'Contract to Settlement',
            description:
              'We guide you through the paperwork, liaise with solicitors and stay across the process right through to settlement day.',
          },
        ]}
      />

      <ValueProposition
        id="property-types"
        className="section--navy-mid scroll-mt-20"
        eyebrow="Coverage"
        heading="We Work Across All Property Types"
        description="Whether you're selling a family home, a rural acreage, a lifestyle block or an investment property, we have the experience to represent you well across every corner of the South Burnett."
        align="center"
        items={[
          { title: 'Residential homes' },
          { title: 'Rural & acreage properties' },
          { title: 'Lifestyle properties' },
          { title: 'Vacant land' },
          { title: 'Investment properties' },
        ]}
      />

      <ImageGallery
        id="gallery"
        className="scroll-mt-20"
        eyebrow="Gallery"
        heading="The South Burnett"
        description="A part of Queensland worth showing up for."
        images={[
          {
            src: AERIAL_FARMLAND,
            alt: 'Aerial view of South Burnett farmland',
            caption: 'Rural land on the edge of Kingaroy',
          },
          { src: STREET_AT_DUSK, alt: 'A Kingaroy street at dusk', caption: 'A quiet street at sunset' },
        ]}
      />

      <FaqSection
        id="faqs"
        className="section--navy-mid scroll-mt-20"
        eyebrow="FAQs"
        heading="Frequently Asked Questions"
        faqs={SELLING_FAQS}
      />

      <CtaBanner
        id="contact"
        className="scroll-mt-20"
        eyebrow="Contact"
        heading="Ready to talk about selling?"
        description="Whether you're ready to list or just starting to explore your options, a conversation costs you nothing."
        primaryCta={{ label: 'Request an appraisal', to: '/appraisal' }}
        secondaryCta={{ label: 'Call Lucio', to: 'tel:0493035628' }}
        contactName="Lucio Passos"
        contactTitle="Sales Agent, Meet Real Estate"
        contactLinks={[
          { label: 'Mobile', value: '0493 035 628', href: 'tel:0493035628' },
          { label: 'Office', value: '07 3355 7777', href: 'tel:0733557777' },
          { label: 'Email', value: 'lucio@meetrealestate.com.au', href: 'mailto:lucio@meetrealestate.com.au' },
          { label: 'Website', value: 'meetrealestate.com.au', href: 'https://meetrealestate.com.au' },
        ]}
      />
    </main>
  );
}
